import React, { useState } from 'react';
import {
  DateRangeControl,
  Delta,
  InfoTip,
  SectionHeader,
  StatCard,
  defaultRange,
  priorRange,
} from '@/components/shared';
import { Env, ENV_COLORS } from '../shared';
import { useAdminFetch } from '../shared/useAdminFetch';
import { MonitoringStats, useMonitoringStats } from './api/useMonitoringStats';
import { useCampaignStats } from './api/useCampaignStats';

const ENVS: Env[] = ['dev', 'staging', 'prod'];

// ─────────────────────────────────────────────────────────────────────────────
// Local primitives
// ─────────────────────────────────────────────────────────────────────────────

function ComingSoonBadge() {
  return (
    <span className="px-2 py-0.5 bg-amber-950/50 border border-amber-800/40 rounded-md text-xs font-medium text-amber-400">
      Coming soon
    </span>
  );
}

function StackRow({
  name,
  status,
  region,
  updated,
}: {
  name: string;
  status: 'healthy' | 'degraded' | 'unknown';
  region: string;
  updated: string;
}) {
  const statusConfig = {
    healthy:  { dot: 'bg-green-500', label: 'Healthy',  badge: 'bg-green-500/10 text-green-400 border-green-700/30' },
    degraded: { dot: 'bg-red-500',   label: 'Degraded', badge: 'bg-red-500/10 text-red-400 border-red-700/30'       },
    unknown:  { dot: 'bg-gray-500',  label: 'Not tracked yet', badge: 'bg-gray-800 text-gray-400 border-gray-700/30' },
  };
  const s = statusConfig[status];

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-800/60 last:border-0 hover:bg-gray-800/20 transition-colors">
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot} ${status === 'healthy' ? 'shadow-[0_0_6px_rgba(34,197,94,0.6)]' : ''}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-200 truncate">{name}</p>
        <p className="text-xs text-gray-600">{region}</p>
      </div>
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${s.badge}`}>{s.label}</span>
      <span className="text-xs text-gray-600 w-28 text-right flex-shrink-0">{updated}</span>
    </div>
  );
}

function MetricRow({
  label,
  value,
  subtext,
  pending = false,
  untracked = false,
}: {
  label: string;
  value?: string;
  subtext?: string;
  pending?: boolean;
  /** Nothing collects this number yet. Says so instead of showing a value or a loading shimmer. */
  untracked?: boolean;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-800/60 last:border-0">
      <p className="text-sm text-gray-300">{label}</p>
      {untracked ? (
        <p className="text-xs text-gray-500 italic">Not tracked yet</p>
      ) : pending ? (
        <div className="h-5 w-16 bg-gray-800 rounded animate-pulse" />
      ) : (
        <div className="text-right">
          <p className="text-sm font-semibold text-white">{value}</p>
          {subtext && <p className="text-xs text-gray-600">{subtext}</p>}
        </div>
      )}
    </div>
  );
}

function ImGoingSection({ stats, loading }: { stats: MonitoringStats; loading: boolean }) {
  const n = (v: number | null) => (v !== null ? v.toLocaleString() : '—');
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-800/60 last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-800/20 transition-colors"
      >
        <p className="text-sm text-gray-300">&quot;I&apos;m Going&quot; clicks</p>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-gray-800/60">
          <MetricRow label={`"I'm Going" clicks — last 24 hours`} value={n(stats.going_clicks_today)} pending={loading} />
          <MetricRow label={`"I'm Going" clicks — last 7 days`} value={n(stats.going_clicks_this_week)} pending={loading} />
          <MetricRow label={`"I'm Going" clicks — this month`} untracked />
          <MetricRow label={`"I'm Going" clicks — all time`} value={n(stats.going_clicks)} pending={loading} />
        </div>
      )}
    </div>
  );
}

function CheckinsSection() {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-gray-800/60 last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-800/20 transition-colors"
      >
        <p className="text-sm text-gray-300">Check-ins</p>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-gray-800/60">
          <MetricRow label="Check-ins — today"   untracked />
          <MetricRow label="Check-ins — this week" untracked />
          <MetricRow label="Check-ins — this month"  untracked />
          <MetricRow label="Check-ins — all time"    untracked />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Tab
// ─────────────────────────────────────────────────────────────────────────────

export function MonitoringTab(): React.ReactElement {
  // Prod by default: these are the numbers anyone opening Monitoring means.
  const [env, setEnv] = useState<Env>('prod');
  const [[from, to], setRange] = useState<[string | null, string | null]>(defaultRange);
  const { data: stats, loading: statsLoading } = useMonitoringStats(env, from, to);
  const before = priorRange(from, to);
  const prior =
    useAdminFetch<Partial<MonitoringStats>>(
      before && {
        env,
        path: '/admin/monitoring/user-count',
        query: { from: before[0], to: before[1] },
      },
    )?.window ?? null;
  const win = stats.window;
  const allTime = from === null && to === null;
  const { data: campaigns, loading: campaignsLoading } = useCampaignStats(env);

  return (
    <div className="space-y-10">

      {/* ── Environment ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-end gap-2">
        <span className="text-xs text-gray-500">Environment</span>
        <div className="flex gap-1">
          {ENVS.map((e) => (
            <button
              key={e}
              onClick={() => setEnv(e)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md border uppercase ${
                env === e ? ENV_COLORS[e].pill : 'bg-gray-900 border-gray-800 text-gray-500 hover:text-gray-300'
              }`}
            >
              {e}
            </button>
          ))}
        </div>
      </div>

      {/* ── Key Metrics ─────────────────────────────────────────────────────── */}
      <div>
        <SectionHeader
          title="Key Metrics"
          description="High-level numbers across users and in-app activity."
        />

        {/* Windowed: the same activity inside the selected range. */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">In the selected range</p>
          <DateRangeControl from={from} to={to} onChange={(f, t) => setRange([f, t])} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {(
            [
              ['New users', 'window_new_users', allTime ? stats.user_count : win?.new_users, prior?.new_users, ''],
              ['"I\'m Going" clicks', 'going_clicks', allTime ? stats.going_clicks : win?.going_clicks, prior?.going_clicks, ''],
              ['Venue time', 'venue_hours', allTime ? stats.venue_hours : win?.venue_hours, prior?.venue_hours, 'h'],
            ] as const
          ).map(([label, info, value, was, unit]) => (
            <div key={label} className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                {label}
                <InfoTip metric={info} />
              </p>
              {statsLoading ? (
                <div className="h-7 w-20 bg-gray-800 rounded animate-pulse" />
              ) : (
                <p className="text-2xl font-bold text-white tabular-nums flex items-center gap-2 flex-wrap">
                  {value !== null && value !== undefined ? `${value.toLocaleString()}${unit}` : '—'}
                  <Delta current={value} prior={was} />
                </p>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">Totals</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label={<>Total Users<InfoTip metric="total_users" /></>}
            value={stats.user_count !== null ? stats.user_count.toLocaleString() : '—'}
            subtext="Registered accounts"
            accent="violet"
            pending={statsLoading}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
            }
          />
          <StatCard
            label={<>&quot;I&apos;m Going&quot; Clicks<InfoTip metric="going_clicks" /></>}
            value={stats.going_clicks !== null ? stats.going_clicks.toLocaleString() : '—'}
            subtext="All time"
            accent="pink"
            pending={statsLoading}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.625m0 0H3.75m1.875 0c-.621 0-1.125.504-1.125 1.125v4.5c0 .621.504 1.125 1.125 1.125h1.5c.621 0 1.125-.504 1.125-1.125v-4.5c0-.621-.504-1.125-1.125-1.125H5.625z" />
              </svg>
            }
          />
          <StatCard
            label={<>Total Venue Time<InfoTip metric="venue_hours" /></>}
            value={stats.venue_hours !== null ? `${stats.venue_hours.toLocaleString()}h` : '—'}
            subtext="Cumulative across all users"
            accent="cyan"
            pending={statsLoading}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            label="Instagram Story Tags"
            value="—"
            subtext="Brand mentions"
            accent="amber"
            untracked
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
              </svg>
            }
          />
        </div>
      </div>

      {/* ── Two-column: App Activity + Instagram ────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* App Activity */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <SectionHeader
              title="App Activity"
              description='In-app engagement signals.'
            />
            <ComingSoonBadge />
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <ImGoingSection stats={stats} loading={statsLoading} />
            <MetricRow label="Avg. session length"             subtext="minutes" untracked />
            <MetricRow label="Total venue time — today"        subtext="hours" untracked />
            <MetricRow
              label="Total venue time — all time"
              value={stats.venue_hours !== null ? `${stats.venue_hours.toLocaleString()}h` : '—'}
              subtext="hours"
              pending={statsLoading}
            />
            <CheckinsSection />

          </div>
        </div>

        {/* Instagram */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <SectionHeader
              title="Instagram"
              description="Story tags and brand mentions."
            />
            <ComingSoonBadge />
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <MetricRow label="Story tags — last 24h"    untracked />
            <MetricRow label="Story tags — last 7 days" untracked />
            <MetricRow label="Story tags — all time"    untracked />
            <MetricRow label="Top tagged venue"         untracked />
            <MetricRow label="Unique taggers"           untracked />
            <MetricRow label="Avg. tags per day"        untracked />
          </div>
          <div className="mt-3 p-4 bg-gray-900 border border-gray-800 rounded-2xl">
            <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wider">Integration status</p>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-300 font-medium">Instagram Graph API</p>
                <p className="text-xs text-gray-600">Not connected — requires Business account + token</p>
              </div>
              <span className="ml-auto px-2.5 py-1 bg-gray-800 border border-gray-700/50 rounded-full text-xs text-gray-500">Disconnected</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── AWS Infrastructure ───────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <SectionHeader
            title="AWS Infrastructure"
            description="CloudFormation stack health and Lambda function status."
          />
          <ComingSoonBadge />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* CloudFormation Stacks */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">CloudFormation Stacks</p>
            <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
              <StackRow name="prod-adminsite-api"          status="unknown" region="us-east-1" updated="" />
              <StackRow name="prod-adminsite-lambdas-dashboard" status="unknown" region="us-east-1" updated="" />
              <StackRow name="prod-adminsite-lambdas-businesses" status="unknown" region="us-east-1" updated="" />
              <StackRow name="prod-adminsite-lambdas-other"     status="unknown" region="us-east-1" updated="" />
              <StackRow name="prod-adminsite-auth"         status="unknown" region="us-east-1" updated="" />
            </div>
          </div>

          {/* Lambda Health */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3">Lambda Functions</p>
            <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
              <MetricRow label="Total invocations — today"     untracked />
              <MetricRow label="Error rate — last 24h"         subtext="% of invocations" untracked />
              <MetricRow label="P99 duration"                  subtext="ms" untracked />
              <MetricRow label="Throttle count — last 24h"     untracked />
              <MetricRow label="Cold starts — last 24h"        untracked />
            </div>
          </div>
        </div>

        {/* Additional AWS stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <StatCard
            label="DynamoDB Read Units"
            value="—"
            subtext="Consumed today"
            accent="blue"
            untracked
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 5.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
              </svg>
            }
          />
          <StatCard
            label="API Gateway Requests"
            value="—"
            subtext="Last 24 hours"
            accent="green"
            untracked
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
              </svg>
            }
          />
          <StatCard
            label={<>Month-to-date cost<InfoTip metric="mtd_cost" /></>}
            value={stats.estimated_monthly_cost !== null ? `$${stats.estimated_monthly_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
            subtext="AWS Spend this Month"
            accent="amber"
            pending={statsLoading}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>
      </div>

      {/* ── QR Campaigns ─────────────────────────────────────────────────────── */}
      <div>
        <SectionHeader
          title="QR Campaigns"
          description="Scan counts for printed and shared QR codes."
        />
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          {campaignsLoading ? (
            <div className="px-4 py-8 flex items-center justify-center">
              <div className="h-5 w-40 bg-gray-800 rounded animate-pulse" />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-500">No campaigns yet — scans will appear here after the first QR scan.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Scans</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Last Scanned</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Created</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c, i) => (
                  <tr key={c.campaign_id} className={`${i < campaigns.length - 1 ? 'border-b border-gray-800/60' : ''} hover:bg-gray-800/20 transition-colors`}>
                    <td className="px-4 py-3.5">
                      <p className="font-medium text-gray-200">{c.campaign_name || c.campaign_id}</p>
                      <p className="text-xs text-gray-600 mt-0.5">{c.campaign_id}</p>
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <span className="text-lg font-bold text-green-400">{c.scan_count.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right hidden sm:table-cell">
                      <span className="text-gray-400 text-xs">
                        {c.last_scanned_at ? new Date(c.last_scanned_at).toLocaleString() : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right hidden md:table-cell">
                      <span className="text-gray-600 text-xs">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── User Growth ──────────────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <SectionHeader
            title="User Growth"
            description="Account registrations and retention over time."
          />
          <ComingSoonBadge />
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-800">
            {[
              { label: 'New users today',        sub: 'registrations' },
              { label: 'New users this week',    sub: 'last 7 days' },
              { label: 'New users this month',   sub: 'last 30 days' },
              { label: 'Active users',           sub: 'checked in last 30d' },
            ].map(({ label, sub }) => (
              <div key={label} className="px-5 py-5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">{label}</p>
                <p className="text-sm font-medium text-gray-500 italic">Not tracked yet</p>
                <p className="text-xs text-gray-700 mt-1.5">{sub}</p>
              </div>
            ))}
          </div>
          {/* Chart placeholder */}
          <div className="border-t border-gray-800 px-5 py-4">
            <p className="text-xs text-gray-600 mb-3">Registrations — last 30 days</p>
            <div className="h-32 bg-gray-800/40 rounded-xl flex items-center justify-center border border-gray-800 border-dashed">
              <p className="text-xs text-gray-500 italic">Not tracked yet</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
